import React from 'react';
import type { ModuleDefinition } from './moduleRegistry';
import type { ReportData } from '../tokens/types';
import { PdfSection, PdfKpiGrid, PdfDataTable, PdfDivider } from '../primitives/PdfPrimitives';
import { ScreenSection, ScreenKpiGrid, ScreenDataTable, ScreenDivider } from '../primitives/ScreenPrimitives';
import { View, Text } from '@react-pdf/renderer';
import { colors, fontFamily, typeScale } from '../tokens/designTokens';
import { fmtAmtCr, fmtDate } from './formatUtils';

const PdfFunding: React.FC<{ data: ReportData; index: string; breakBefore?: boolean }> = ({ data, index, breakBefore }) => {
    const f = data.funding;
    if (!f) return null;
    const rows = f.fundingRows.map((r) => ({
        date: fmtDate(r.date),
        amount: r.amount,
        instrument: r.instrument,
        type: r.type,
    }));
    const debt = f.debentureRows.map((r) => ({
        date: fmtDate(r.date),
        amount: r.amount,
        instrument: r.instrument,
        type: r.type,
    }));
    return (
        <PdfSection kicker="Capital flows" title="Funding & debenture history" index={index} breakBefore={breakBefore}>
            <PdfKpiGrid
                items={[
                    { label: 'Total funding raised', value: fmtAmtCr(f.totalFundingCr) },
                    { label: 'Total debentures', value: fmtAmtCr(f.totalDebentureCr) },
                    { label: 'Rounds recorded', value: String(f.fundingRows.length) },
                    { label: 'Last updated', value: fmtDate(f.lastUpdatedAt) },
                ]}
            />
            {rows.length ? (
                <View style={{ marginTop: 10 }} minPresenceAhead={70}>
                    {/* minPresenceAhead anchors the "Equity funding" heading to a
                        page that has at least one row's worth of vertical space
                        below it — prevents the heading orphaning on the previous
                        page while the table starts on the next. The table itself
                        is unaffected and continues to flow naturally. */}
                    <Text style={{ fontFamily: fontFamily.sansBold, fontSize: typeScale.body, marginBottom: 4 }}>Equity funding</Text>
                    <PdfDataTable
                        columns={[
                            { key: 'date', header: 'Date', width: '18%' },
                            { key: 'amount', header: 'Amount', width: '22%' },
                            { key: 'instrument', header: 'Instrument' },
                            { key: 'type', header: 'Type', width: '22%' },
                        ]}
                        rows={rows}
                    />
                </View>
            ) : null}
            {debt.length ? (
                <View minPresenceAhead={70}>
                    <PdfDivider />
                    <Text style={{ fontFamily: fontFamily.sansBold, fontSize: typeScale.body, marginBottom: 4 }}>Debenture issuances</Text>
                    <PdfDataTable
                        columns={[
                            { key: 'date', header: 'Date', width: '18%' },
                            { key: 'amount', header: 'Amount', width: '22%' },
                            { key: 'instrument', header: 'Instrument' },
                            { key: 'type', header: 'Type', width: '22%' },
                        ]}
                        rows={debt}
                    />
                </View>
            ) : null}
            <Text style={{ fontSize: typeScale.micro, color: colors.mutedSoft, marginTop: 4 }}>
                Amounts as reported in MCA filings. A dash (—) indicates unknown or unreported.
            </Text>
        </PdfSection>
    );
};

const ScreenFunding: React.FC<{ data: ReportData; index: string }> = ({ data, index }) => {
    const f = data.funding;
    if (!f) return null;
    const rows = f.fundingRows.map((r) => ({
        date: fmtDate(r.date),
        amount: r.amount,
        instrument: r.instrument,
        type: r.type,
    }));
    const debt = f.debentureRows.map((r) => ({
        date: fmtDate(r.date),
        amount: r.amount,
        instrument: r.instrument,
        type: r.type,
    }));
    return (
        <ScreenSection kicker="Capital flows" title="Funding & debenture history" index={index}>
            <ScreenKpiGrid
                items={[
                    { label: 'Total funding raised', value: fmtAmtCr(f.totalFundingCr) },
                    { label: 'Total debentures', value: fmtAmtCr(f.totalDebentureCr) },
                    { label: 'Rounds recorded', value: String(f.fundingRows.length) },
                    { label: 'Last updated', value: fmtDate(f.lastUpdatedAt) },
                ]}
            />
            {rows.length ? (
                <div style={{ marginTop: 10 }}>
                    <div style={{ fontFamily: 'Gilroy-Bold', marginBottom: 4 }}>Equity funding</div>
                    <ScreenDataTable
                        columns={[
                            { key: 'date', header: 'Date', width: '18%' },
                            { key: 'amount', header: 'Amount', width: '22%' },
                            { key: 'instrument', header: 'Instrument' },
                            { key: 'type', header: 'Type', width: '22%' },
                        ]}
                        rows={rows}
                    />
                </div>
            ) : null}
            {debt.length ? (
                <>
                    <ScreenDivider />
                    <div style={{ fontFamily: 'Gilroy-Bold', marginBottom: 4 }}>Debenture issuances</div>
                    <ScreenDataTable
                        columns={[
                            { key: 'date', header: 'Date', width: '18%' },
                            { key: 'amount', header: 'Amount', width: '22%' },
                            { key: 'instrument', header: 'Instrument' },
                            { key: 'type', header: 'Type', width: '22%' },
                        ]}
                        rows={debt}
                    />
                </>
            ) : null}
        </ScreenSection>
    );
};

export const fundingModule: ModuleDefinition = {
    meta: {
        id: 'funding',
        title: 'Funding history',
        description: 'Equity + debenture issuances.',
        dataSource: 'MCA Funding History',
    },
    hasData: (d) => !!d.funding && (d.funding.fundingRows.length > 0 || d.funding.debentureRows.length > 0),
    Pdf: PdfFunding,
    Screen: ScreenFunding,
};
